import { useState, useEffect, useCallback } from 'react';
import { checkCoverage, CoverageReport } from '../../services/rosterService';
import { format } from 'date-fns';

interface CoverageWidgetProps {
    date: Date;
    onUpdate?: () => void;
}

const DISPLAY_LABELS: Record<string, string> = {
    'CLEARER': 'LGW',
    'LHR': 'LHR',
    'OFFICE': 'Office',
    'WAREHOUSE': 'WH',
    'CONTROL': 'Control',
};

const CoverageWidget = ({ date, onUpdate }: CoverageWidgetProps) => {
    const [report, setReport] = useState<CoverageReport | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchCoverage = useCallback(async () => {
        setLoading(true);
        try {
            const data = await checkCoverage(date);
            setReport(data);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Failed to check coverage", error);
        } finally {
            setLoading(false);
        }
    }, [date, onUpdate]);

    useEffect(() => {
        fetchCoverage();
        const interval = setInterval(fetchCoverage, 30000);
        return () => clearInterval(interval);
    }, [fetchCoverage]);

    if (loading || !report) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="text-xs text-gray-400">Loading coverage...</div>
            </div>
        );
    }

    const getMinRequired = (type: string): number => {
        switch (type) {
            case 'CLEARER':
                return 2;
            case 'CONTROL':
            case 'OFFICE':
            case 'WAREHOUSE':
                return 1;
            default:
                return 1;
        }
    };

    const getCoverageStatus = (type: string, current: number): 'sufficient' | 'warning' | 'critical' => {
        const required = getMinRequired(type);
        if (current >= required) return 'sufficient';
        if (current === required - 1) return 'warning';
        return 'critical';
    };

    const getLabel = (type: string): string => DISPLAY_LABELS[type] || type;

    const filteredAlerts = report.alerts;
    const filteredGaps = report.skillGaps || [];

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 h-full">
            <h3 className="font-bold text-gray-800 mb-3 border-b pb-2 flex items-center justify-between">
                <span>Coverage: {format(date, 'MMM d')}</span>
                {filteredAlerts.length > 0 && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                        ⚠ {filteredAlerts.length} Alert{filteredAlerts.length !== 1 ? 's' : ''}
                    </span>
                )}
            </h3>

            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                {Object.entries(report.coverage)
                    .filter(([_type]) => true)
                    .map(([type, count]) => {
                    const required = getMinRequired(type);
                    const status = getCoverageStatus(type, count);
                    const statusColors = {
                        sufficient: 'bg-green-50 border-green-200 text-green-700',
                        warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
                        critical: 'bg-red-50 border-red-200 text-red-700',
                    };

                    return (
                        <div
                            key={type}
                            className={`p-2 rounded border ${statusColors[status]}`}
                        >
                            <span className="text-gray-600 block text-xs font-medium">{getLabel(type)}</span>
                            <span className={`font-bold text-sm ${status === 'sufficient' ? 'text-green-700' : status === 'warning' ? 'text-yellow-700' : 'text-red-700'}`}>
                                {count} / {required}
                            </span>
                            {status !== 'sufficient' && (
                                <span className="text-xs block mt-1">
                                    Gap: {required - count}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {filteredGaps.length > 0 && (
                <div className="mb-4 bg-orange-50 border border-orange-200 rounded p-3">
                    <h4 className="text-orange-800 font-semibold text-xs mb-2">Skill Gaps</h4>
                    <ul className="space-y-1">
                        {filteredGaps.map((gap, idx) => (
                            <li key={idx} className="text-xs text-orange-700">
                                {getLabel(gap.shiftType)}: {gap.gap} staff short ({gap.current}/{gap.required})
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {filteredAlerts.length > 0 ? (
                <div className="bg-red-50 border border-red-100 rounded p-3">
                    <h4 className="text-red-800 font-semibold text-xs mb-1">Alerts</h4>
                    <ul className="list-disc list-inside text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {filteredAlerts.map((alert, idx) => (
                            <li key={idx}>{alert}</li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="bg-green-50 border border-green-100 rounded p-3 text-center">
                    <span className="text-green-700 text-xs font-medium">✓ All coverage requirements met</span>
                </div>
            )}
        </div>
    );
};

export default CoverageWidget;
